import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["onSuccess", "skip"];
import { registerQuery, useQuery } from 'data-fetching-client';
import { Segment } from 'crm-index-visualization-core/core/model/segment';
import { tryOr } from 'crm-index-visualization-core/core/error/errorUtils';
import { ViewDraft } from 'crm-index-visualization-core/core/model/viewDraft';
import { useClientStorage, useDraftContext } from 'crm-index-visualization-core/core/data/VizCoreProvider';
import { useMemo } from 'react';
import { useView } from './useView';
import { usePipelineProperty } from './usePipelineProperty';

// Internal query - now exported for cache updates
export const getSegmentQueryInternal = registerQuery({
  fieldName: 'vizcoreSegment',
  fetcher: async ({
    draftContext,
    clientStorage,
    view,
    pipelinePropertyName
  }) => {
    // Detect bypass mode from the hash key
    const isBypassMode = draftContext.editHash === 'bypass-draft-active';
    if (isBypassMode) {
      return Segment.fromBEView(view, pipelinePropertyName);
    }

    // If we have an editHash, try to get draft data from client cache first
    const {
      editHash
    } = draftContext;
    if (editHash) {
      const {
        segmentHashOrId
      } = await tryOr({}, () => ViewDraft.fromClientCache(editHash, clientStorage));
      if (segmentHashOrId) {
        const draftSegment = await tryOr(null, () => Segment.fromClientCache(segmentHashOrId, clientStorage));
        if (draftSegment) {
          return draftSegment.with({
            pipelinePropertyName
          });
        }
      }
    }
    // Fall back to fetching from BE if no draft exists
    return Segment.fromBEView(view, pipelinePropertyName);
  },
  args: ['draftContext', 'clientStorage', 'view', 'pipelinePropertyName']
});
const EMPTY_OBJECT = {};

/**
 * Hook to fetch and manage the current segment data, subjected to draft data if an editHash is present.
 *
 * This hook is built on Data Fetching Client (DFC) but have slightly cleaner API, i.e it doesn't expose the internal field keys.
 *
 * ## Segment
 *
 * A "segment" is a collection of data that limits the set of CRM data that is displayed.
 * It includes search terms, filters, and sorts. The `Segment` class provides a comprehensive data model that encapsulates
 * all these aspects.
 *
 * Internally, Segment is extracted from the Index View system (aka Sales views, hereby referred to as Backend View).
 * There is a significant of legacy custom behavior that this hook encapsulates and handle internally, such as custom filter rules, etc. Thus it is preferred over reading the Backend View directly.
 *
 * ## Draft Mechanism
 *
 * The hook transparently handles draft data through an edit hash system:
 *
 * 1. When a segment is edited, it's saved in a client-side storage (defaults to localStorage) with a unique hash (SHA-256)
 * 2. This hash is part of another hashed composite, which ultimately becomes the global "editHash". This is equivalent to the "version key" of a form.
 * 3. The Segmentation returned by this hook observes the editHash and will return draft data if it's present. It falls back to the Segmentation extracted from the backend view if no draft is present.
 *
 * This allows us to implement a "draft" mechanism, hide the rapidly evolved "View" model, and provide an easier way to detect if the segment has changed or has changes to be saved.
 *
 * @param options - Query options (excluding variables)
 * @throws {Error} If used outside of a VizCoreProvider
 * @returns {Object} Query result with segment data directly
 *   - data: Segment | null - The current segment if available
 *   - loading: boolean - Whether the query is loading
 *   - error: ApolloError | undefined - Any query errors
 *
 * ## Basic Usage
 *
 * ```tsx
 * const { data: segment, loading, error } = useSegment();
 *
 * if (loading) return <Spinner />;
 * if (error) return <ErrorMessage error={error} />;
 * if (!segment) return <EmptyState />;
 *
 * return (
 *   <div>
 *     <h1>{segment.search}</h1>
 *     <p>Pipeline ID: {segment.pipelineId}</p>
 *     <p>Filter groups: {segment.advancedFilterGroups.length}</p>
 *
 *     // Use consolidated filters for API calls
 *     <SearchResults
 *       filters={segment.consolidatedFilters({ __hs__NOW: Date.now() })}
 *     />
 *   </div>
 * );
 * ```
 *
 * ## Editing Segment Data
 *
 * Use the `useMutateSegment` hook to modify segment data:
 *
 * ```tsx
 * const { data: segment } = useSegment();
 * const [saveSegment, { loading }] = useMutateSegment();
 *
 * const updateSearch = (newSearch) => {
 *   if (segment) {
 *     // Always use with() for immutable updates
 *     saveSegment(segment.with({ search: newSearch }));
 *   }
 * };
 * ```
 *
 * ## Best Practices
 *
 * 1. **Use the `with()` method for immutable updates:**
 *    ```tsx
 *    // Good - creates a new Segment object
 *    saveSegment(segment.with({ search: newTerm }));
 *    ```
 *
 * 2. **Use `consolidatedFilters()` for API calls (for example to crm search):**
 *    ```tsx
 *    // This generates a combined representation of all filters
 *    segment.consolidatedFilters({ __hs__NOW: Date.now() })
 *    ```
 *
 * For more details on Segment properties and methods, see the Segment class JSDoc
 * in core/model/segment.ts.
 *
 * @see Segment
 */
export function useSegment(options = EMPTY_OBJECT) {
  const [draftContext] = useDraftContext();
  const clientStorage = useClientStorage();
  const {
    data: {
      vizcoreCurrentView
    } = {},
    loading: viewLoading,
    error: viewError
  } = useView({
    onError: options.onError
  });
  const {
    data: pipelinePropertyName,
    loading: pipelinePropertyLoading
  } = usePipelineProperty(draftContext.objectTypeId);
  const shouldSkip = !vizcoreCurrentView || pipelinePropertyLoading;
  const queryOptions = useMemo(() => {
    const {
        onSuccess,
        skip
      } = options,
      restOptions = _objectWithoutPropertiesLoose(options, _excluded);
    return Object.assign({}, restOptions, {
      skip: shouldSkip || skip,
      onCompleted: responseData => {
        if (responseData !== null && responseData !== void 0 && responseData.vizcoreSegment && onSuccess) {
          onSuccess(responseData.vizcoreSegment);
        }
      },
      variables: {
        draftContext,
        clientStorage,
        view: vizcoreCurrentView,
        pipelinePropertyName
      }
    });
  }, [options, draftContext, clientStorage, vizcoreCurrentView, shouldSkip, pipelinePropertyName]);
  const {
    data,
    previousData,
    loading,
    error
  } = useQuery(getSegmentQueryInternal, queryOptions);
  return useMemo(() => {
    const transformedData = (data === null || data === void 0 ? void 0 : data.vizcoreSegment) || (previousData === null || previousData === void 0 ? void 0 : previousData.vizcoreSegment) || null;
    const finalError = viewError !== null && viewError !== void 0 ? viewError : error;
    const isLoading = viewLoading || pipelinePropertyLoading || loading;
    return {
      data: transformedData,
      loading: isLoading,
      error: finalError
    };
  }, [data === null || data === void 0 ? void 0 : data.vizcoreSegment, previousData === null || previousData === void 0 ? void 0 : previousData.vizcoreSegment, viewError, error, viewLoading, pipelinePropertyLoading, loading]);
}